/** Wiki Health: findings about the project's wiki with open, dismiss, and propose-fix actions (child route of the app shell) */

import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  checkScope,
  HEALTH_DEFAULT_STALE_DAYS,
  PERMISSIONS,
  type HealthAutoFixResult,
  type HealthFinding,
  type HealthFindingType,
  type WikiWriteResult,
} from '@agentwiki/shared'
import { useAuth } from '../hooks/use-auth'
import { useHealthAction, useHealthReport } from '../hooks/use-health-ask'
import { projectPath, projectReviewPath } from '../components/shell/nav-config'
import { badgeClass, buttonPrimaryClass, buttonSecondaryClass, errorTextClass, hintClass } from '../components/ui/styles'
import { cn } from '../lib/utils'
import { useT, type TranslationKey } from '../i18n'

const TYPES: HealthFindingType[] = ['superseded_source', 'contradiction', 'stale_page', 'search_gap']
const STALE_OPTIONS = [14, HEALTH_DEFAULT_STALE_DAYS, 60, 90]
const PAGE_SIZE = 50

function FindingCard({ title, children, actions, dismissed }: { title: ReactNode; children?: ReactNode; actions: ReactNode; dismissed: boolean }) {
  const t = useT()
  return (
    <li className={cn('space-y-2 px-4 py-3', dismissed && 'opacity-70')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="min-w-0 font-medium text-fg">{title}</p>
        {dismissed && <span className={badgeClass}>{t('health.dismissed')}</span>}
      </div>
      {children}
      <div className="flex flex-wrap gap-2">{actions}</div>
    </li>
  )
}

export function ProjectHealthPage() {
  const t = useT()
  const { role } = useAuth()
  const { projectSlug = 'default' } = useParams<{ projectSlug: string }>()
  const [type, setType] = useState<HealthFindingType | undefined>()
  const [staleDays, setStaleDays] = useState(HEALTH_DEFAULT_STALE_DAYS)
  const [includeDismissed, setIncludeDismissed] = useState(false)
  const [offset, setOffset] = useState(0)
  const { data, isLoading, error, isFetching } = useHealthReport(projectSlug, { type, staleDays, includeDismissed, limit: PAGE_SIZE, offset })
  const action = useHealthAction(projectSlug)
  const navigate = useNavigate()
  // Fixes waiting for a reviewer land in the Sources screen's "Waiting for the wiki" tab
  const reviewAction = { label: t('health.proposed.view'), onClick: () => navigate(projectReviewPath(projectSlug)) }
  // Dismissing the last findings of a later page leaves it empty: step back to the last page that has some
  useEffect(() => {
    if (data && offset > 0 && offset >= data.total) setOffset(Math.max(0, Math.floor((data.total - 1) / PAGE_SIZE) * PAGE_SIZE))
  }, [data, offset])
  const canAct = role ? checkScope(PERMISSIONS[role] ?? [], 'doc:update') : false

  const run = async (input: Parameters<typeof action.mutateAsync>[0]) => {
    try {
      const result = await action.mutateAsync(input)
      if (input.action === 'fix') {
        const applied = (result as WikiWriteResult).status === 'applied'
        toast.success(t(applied ? 'health.proposed.applied' : 'health.proposed.pending'), applied ? undefined : { action: reviewAction })
      } else if (input.action === 'fix-certain') {
        const fixes = result as HealthAutoFixResult
        const pending = fixes.proposals.filter((p) => p.status === 'pending').length
        toast.success(fixes.proposals.length || fixes.failed.length
          ? t('health.fixCertain.done', {
            applied: fixes.proposals.filter((p) => p.status === 'applied').length,
            pending,
            failed: fixes.failed.length,
          })
          : t('health.fixCertain.none'), pending ? { action: reviewAction } : undefined)
      }
    } catch (err) {
      toast.error(t('health.action.failed', { message: err instanceof Error ? err.message : String(err) }))
    }
  }

  const pageLink = (slug: string, label?: string) => (
    <Link to={projectPath(projectSlug, `wiki/${slug}`)} className={buttonSecondaryClass}>{label ?? t('health.action.open')}</Link>
  )
  const dismissButton = (f: HealthFinding) => canAct && (
    <button
      type="button"
      disabled={action.isPending}
      onClick={() => void run({ action: f.dismissed ? 'restore' : 'dismiss', key: f.key })}
      className={buttonSecondaryClass}
    >
      {t(f.dismissed ? 'health.action.restore' : 'health.action.dismiss')}
    </button>
  )

  const renderFinding = (f: HealthFinding) => {
    switch (f.type) {
      case 'superseded_source':
        return (
          <FindingCard
            key={f.key}
            dismissed={f.dismissed}
            title={t('health.superseded.title', { page: f.page.title })}
            actions={<>
              {pageLink(f.page.slug)}
              <Link to={projectPath(projectSlug, `sources/${f.source.id}`)} className={buttonSecondaryClass}>{f.source.filename}</Link>
              {canAct && f.replacement && !f.dismissed && (
                <button type="button" disabled={action.isPending} onClick={() => void run({ action: 'fix', key: f.key })} className={buttonPrimaryClass}>
                  {t('health.action.propose')}
                </button>
              )}
              {dismissButton(f)}
            </>}
          >
            <p className="text-sm text-fg-muted">
              {t('health.superseded.detail', {
                source: f.source.title,
                count: f.sentences,
                replacement: f.replacement ? f.replacement.title : t('health.superseded.noReplacement'),
              })}
            </p>
            <p className={cn('text-xs', f.certain ? 'text-fg' : 'text-fg-subtle')}>
              {t(f.certain ? 'health.superseded.certain' : 'health.superseded.uncertain')}
            </p>
          </FindingCard>
        )
      case 'contradiction':
        return (
          <FindingCard
            key={f.key}
            dismissed={f.dismissed}
            title={t('health.contradiction.title', { reason: t(`health.contradiction.${f.reason}` as TranslationKey) })}
            actions={<>{pageLink(f.a.page.slug, f.a.page.title)}{pageLink(f.b.page.slug, f.b.page.title)}{dismissButton(f)}</>}
          >
            <ul className="space-y-1 text-sm text-fg-muted">
              <li><span className="font-medium text-fg">{f.a.page.title}:</span> {f.a.sentence}</li>
              <li><span className="font-medium text-fg">{f.b.page.title}:</span> {f.b.sentence}</li>
            </ul>
            <p className={hintClass}>{t('health.contradiction.hint')}</p>
          </FindingCard>
        )
      case 'stale_page':
        return (
          <FindingCard
            key={f.key}
            dismissed={f.dismissed}
            title={t('health.stale.title', { page: f.page.title })}
            actions={<>{pageLink(f.page.slug)}{dismissButton(f)}</>}
          >
            <p className="text-sm text-fg-muted">{t(`health.stale.${f.reason}`, { days: f.days })}</p>
          </FindingCard>
        )
      case 'search_gap':
        return (
          <FindingCard
            key={f.key}
            dismissed={f.dismissed}
            title={t('health.gap.title', { query: f.query })}
            actions={<>
              <Link to={projectPath(projectSlug, 'sources')} className={buttonSecondaryClass}>{t('nav.sources')}</Link>
              {dismissButton(f)}
            </>}
          >
            <p className="text-sm text-fg-muted">{t('health.gap.detail', { count: f.count, kinds: f.kinds.join(', ') })}</p>
          </FindingCard>
        )
    }
  }

  const total = data ? TYPES.reduce((sum, ty) => sum + data.counts[ty], 0) : 0

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 md:px-8 md:py-10">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{t('health.title')}</h1>
        <p className="max-w-2xl text-sm text-fg-muted">{t('health.subtitle')}</p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap rounded-lg border border-border p-0.5" role="group">
          {[undefined, ...TYPES.filter((ty) => ty !== 'search_gap' || data?.gapsIncluded !== false)].map((value) => (
            <button
              key={value ?? 'all'}
              type="button"
              aria-pressed={type === value}
              onClick={() => { setType(value); setOffset(0) }}
              className={`cursor-pointer rounded-md px-3 py-1.5 text-sm ${type === value ? 'bg-surface-hover text-fg' : 'text-fg-muted hover:text-fg'}`}
            >
              {value ? t(`health.filter.${value}`, { count: data?.counts[value] ?? 0 }) : t('health.filter.all', { count: total })}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-fg-muted">
          {t('health.staleDays')}
          <select
            value={staleDays}
            onChange={(e) => { setStaleDays(Number(e.target.value)); setOffset(0) }}
            className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-fg"
          >
            {STALE_OPTIONS.map((d) => <option key={d} value={d}>{t('health.staleDays.option', { days: d })}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-fg-muted">
          <input type="checkbox" checked={includeDismissed} onChange={(e) => { setIncludeDismissed(e.target.checked); setOffset(0) }} />
          {t('health.showDismissed', { count: data?.dismissed ?? 0 })}
        </label>
      </div>

      {canAct ? (
        <div className="flex flex-col gap-2 rounded-xl border border-border bg-surface-raised p-4 md:flex-row md:items-center md:justify-between">
          <p className={hintClass}>{t('health.fixCertain.hint')}</p>
          <button type="button" disabled={action.isPending} onClick={() => void run({ action: 'fix-certain' })} className={cn(buttonPrimaryClass, 'shrink-0')}>
            {t('health.fixCertain')}
          </button>
        </div>
      ) : (
        <p className={hintClass}>{t('health.needsEditor')}</p>
      )}

      {isLoading ? (
        <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : error || !data ? (
        <p role="alert" className={errorTextClass}>{t('health.loadError')}</p>
      ) : (
        <>
          {data.truncated && <p className={hintClass}>{t('health.truncated')}</p>}
          {data.findings.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border p-6 text-sm text-fg-muted">{t('health.empty')}</p>
          ) : (
            <ul aria-busy={isFetching} className="divide-y divide-border rounded-xl border border-border">
              {data.findings.map(renderFinding)}
            </ul>
          )}
          {data.total > PAGE_SIZE && (
            <nav className="flex items-center gap-3 text-sm text-fg-muted" aria-label={t('health.pages')}>
              <button type="button" onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))} disabled={offset === 0} className={buttonSecondaryClass}>
                {t('health.previous')}
              </button>
              <span className="tabular-nums">{t('health.range', { from: offset + 1, to: offset + data.findings.length, total: data.total })}</span>
              <button type="button" onClick={() => setOffset((o) => o + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= data.total} className={buttonSecondaryClass}>
                {t('health.next')}
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  )
}
